// React and hooks
import React, { useState, useRef, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { auth } from "../../firebase.js";

// Components
import Sidebar from "../shared/Sidebar.js";
import { useContextMenu } from "../shared/ContextMenu";
import { useQuickLinks } from "../../contexts/QuickLinksContext";

// Styles
import "../../css/DocumentationMainPage.css";
import "../../css/Create.css";
import "../../css/Help.css";
import "../../css/HomePage.css"
import "../../css/SupportTicket.css";
import "../../css/Document.css";

function SupportTicket() {

        const [email, setEmail] = useState("")
        const [shortDescription, setShortDescription] = useState("")
        const [message, setMessage] = useState("")
        const { enhancedRightClickEnabled, emailVerified, emailVerifiedLoading } = useQuickLinks();
        const [uploadedFiles, setUploadedFiles] = useState([]);
        const [isSubmitting, setIsSubmitting] = useState(false);
        const [submitMessage, setSubmitMessage] = useState("");
        const [showSuccessMessage, setShowSuccessMessage] = useState(false);
        const navigate = useNavigate()
        const fileInputRef = useRef(null);
        const [authWarning, setAuthWarning] = useState(null);

        // Theme cleanup on unmount


        // Show warning modal if user is not authenticated
        useEffect(() => {
                const unsubscribe = auth.onAuthStateChanged((user) => {
                        if (!user) setAuthWarning('unauthenticated');
                });
                return () => unsubscribe();
        }, []);

        // Show warning modal if user's email is not verified
        useEffect(() => {
                if (emailVerifiedLoading) return;
                const user = auth.currentUser;
                if (!user) return;
                setAuthWarning(emailVerified ? null : 'unverified');
        }, [emailVerified, emailVerifiedLoading]);


        const loggedOutOptions = [
                {
                        label: "Home",
                        icon: <i className="fas fa-home"></i>,
                        url: "/",
                        onClick: () => navigate("/")
                },
                {
                        label: "Join Discord",
                        icon: <i className="fab fa-discord"></i>,
                        url: "https://discord.gg/DYkESHwgVA",
                        onClick: () => window.open("https://discord.gg/DYkESHwgVA", "_blank", "noopener,noreferrer")
                }
        ];

        const menuOptions = loggedOutOptions
        const { showContextMenu, ContextMenuComponent, handleContextMenu } = useContextMenu(menuOptions);

        const handleFileChange = (e) => {
                const files = Array.from(e.target.files);
                const maxFileSize = 8 * 1024 * 1024; 
                const allowedFileTypes = ["application/pdf", "image/jpeg", "image/png"];
        
                let validFiles = files.filter(file => {
                        if (file.size > maxFileSize) {
                                alert(`File ${file.name} exceeds the 8MB limit.`);
                                return false;
                        }
                        if (!allowedFileTypes.includes(file.type)) {
                                alert(`File ${file.name} is not a valid type. Only PDFs, JPEG, and PNG files are allowed.`);
                                return false;
                        }
                        return true;
                });
        
                if (uploadedFiles.length + validFiles.length > 2) {
                        alert("You can only upload up to two files.");
                        validFiles = validFiles.slice(0, 2 - uploadedFiles.length);
                }
        
                setUploadedFiles([...uploadedFiles, ...validFiles]);
        };
        
        const handleFileRemove = (index) => {
                const updatedFiles = uploadedFiles.filter((_, i) => i !== index);
                setUploadedFiles(updatedFiles);
        };

        const triggerFileInput = () => {
                fileInputRef.current.click();
        };

        const handleSubmit = async () => {
                // Check authentication before anything else
                if (!auth.currentUser) {
                        setSubmitMessage("You must be signed in to submit a support ticket. If you're unable to create an account, reach out to us on Discord (discord.gg/DYkESHwgVA).");
                        setShowSuccessMessage(false);
                        return;
                }

                // Validate required fields
                if (!email || !shortDescription || !message) {
                        setSubmitMessage("Please fill out all required fields.");
                        setShowSuccessMessage(false);
                        return;
                }

                // Basic email validation
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (!emailRegex.test(email)) {
                        setSubmitMessage("Please enter a valid email address.");
                        setShowSuccessMessage(false);
                        return;
                }

                setIsSubmitting(true);
                setSubmitMessage("");

                const formData = new FormData();
                formData.append("email", email);
                formData.append("shortDescription", shortDescription);
                formData.append("message", message);
                uploadedFiles.forEach((file, index) => {
                    formData.append(`file${index + 1}`, file);
                });

                try {
                    const token = await auth.currentUser.getIdToken();
                    const response = await fetch("https://bluebirddocumentationadmin.pythonanywhere.com/support/ticket/", {
                            method: "POST",
                            headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `${token}`
                            },
                            body: formData
                    })

                        if (response.ok) {
                                console.log("Successfully submitted ticket.")
                                
                                // Clear the form
                                setEmail("");
                                setShortDescription("");
                                setMessage("");
                                setUploadedFiles([]);
                                
                                // Show success message
                                setSubmitMessage("Support ticket submitted successfully! We'll get back to you as soon as possible.");
                                setShowSuccessMessage(true);
                                
                                // Hide success message after 5 seconds
                                setTimeout(() => {
                                        setSubmitMessage("");
                                        setShowSuccessMessage(false);
                                }, 5000);
                                
                        } else {
                                console.log("Failed to submit ticket.")
                                setSubmitMessage("Failed to submit support ticket. Please try again.");
                                setShowSuccessMessage(false);
                        }
                } catch (error) {
                        console.error("Error in submitting ticket: ", error)
                        setSubmitMessage("An error occurred while submitting your ticket. Please try again.");
                        setShowSuccessMessage(false);
                } finally {
                        setIsSubmitting(false);
                }

        }

        return (
                <div 
                    className="DocumentationMainPage__div"
                    onContextMenu={enhancedRightClickEnabled ? handleContextMenu : undefined}
                >
                        <Sidebar />

                        <div className="DocumentationMainPage__main-content">

                                <div className="Document__div">

                                        <p>Please use the form below to submit a support ticket.</p>

                                        <p className="small">
                                                <strong>Note: </strong> This is not a space to suggest features, ask non-technical questions, etc. If 
                                                that is what you're looking for, please use the <Link to="/help">General Contact Form</Link>.
                                        </p>

                                        <p className="small">
                                                While we try to log errors that users might encounter, it's possible that some might be 
                                                missed. When submitting a support ticket, try to be as detailed as possible, and try to use 
                                                multiple forms of evidence. Below are some suggestions as to what to include.
                                        </p>
                                        <ul>
                                                <li>Upload screenshots</li>
                                                <li>Explain in detail what is happening</li>
                                                <ul>
                                                        <li>
                                                                <strong>Which feature is the unexpected behavior involved with?</strong> Features 
                                                                would include creating, deleting, reading, or modify documents, or could be something 
                                                                else like permissions for other users. <strong>You can use the short description field below to 
                                                                quickly mention the feature involved</strong>.
                                                        </li>
                                                        <li>Did this unexpected behavior occur only once?</li>
                                                        <li>
                                                                If this occurs repeatedly, what are the steps you take to 
                                                                reproduce the issue?
                                                        </li>
                                                        <li>Any additional notes that might come to mind.</li>
                                                </ul>
                                        </ul>

                                        <div className="SupportTicket__form">

                                                <h2 className="SupportTicket__h2">Support Ticket Form</h2>

                                                <div className="SupportTicket__input-container">
                                                        <input
                                                                type="email"
                                                                value={email}
                                                                onChange={(e) => setEmail(e.target.value)}
                                                                required
                                                        />
                                                        <label>E-mail Address</label>
                                                </div>

                                                <div className="SupportTicket__input-container">
                                                        <input
                                                                type="text"
                                                                value={shortDescription}
                                                                onChange={(e) => setShortDescription(e.target.value)}
                                                                required
                                                        />
                                                        <label>Short Description</label>
                                                </div>

                                                <div className="SupportTicket__file-upload-input-container">
                                                        {/* <label>Upload Files (Max: 2 files, up to 8MB each):</label> */}
                                                        <button
                                                                type="button"
                                                                onClick={triggerFileInput}
                                                                className="SupportTicket__file-upload-input"
                                                        >
                                                                Choose Files
                                                        </button>
                                                        <input
                                                                type="file"
                                                                multiple
                                                                accept=".pdf, image/jpeg, image/png"
                                                                onChange={handleFileChange}
                                                                ref={fileInputRef}
                                                                style={{ display: "none" }}
                                                        />
                                                        <div>
                                                                {uploadedFiles.map((file, index) => (
                                                                        <div key={index}>
                                                                        {file.name} 
                                                                        <button style={{ marginLeft: "10px", marginTop: "10px" }} className="SupportTicket__file-upload-input" type="button" onClick={() => handleFileRemove(index)}>Remove</button>
                                                                        </div>
                                                                ))}
                                                        </div>
                                                </div>

                                                <div className="SupportTicket__text-area-input-container">
                                                        <label>Detailed Description</label>
                                                        <textarea
                                                                value={message}
                                                                onChange={(e) => setMessage(e.target.value)}
                                                                rows={7}
                                                                required
                                                        ></textarea>
                                                </div>

                                                                        <button 
                                className="SupportTicket__btn" 
                                onClick={handleSubmit}
                                disabled={isSubmitting}
                        >
                                {isSubmitting ? 'Submitting...' : 'Submit Ticket'}
                        </button>
                        
                        {submitMessage && (
                                <div className={`submit-message ${showSuccessMessage ? 'success' : 'error'}`}>
                                        {submitMessage}
                                </div>
                        )}

                                        </div>

                                </div>

                        </div>

                        {enhancedRightClickEnabled && ContextMenuComponent}

                        {authWarning && (
                                <div style={{
                                        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                                        background: 'rgba(0,0,0,0.75)', zIndex: 9999,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px'
                                }}>
                                        <div style={{
                                                background: '#111827', border: '1px solid rgba(91,146,229,0.35)',
                                                borderRadius: '12px', padding: '32px', maxWidth: '440px',
                                                width: '100%', textAlign: 'center'
                                        }}>
                                                {authWarning === 'unverified' && (
                                                        <div style={{ fontSize: '2.5rem', marginBottom: '12px' }}>✉️</div>
                                                )}
                                                <h3 style={{ color: '#5B92E5', marginBottom: '12px' }}>
                                                        {authWarning === 'unauthenticated' ? 'Sign In Required' : 'Email Verification Required'}
                                                </h3>
                                                <p style={{ color: '#cbd5e1', marginBottom: '24px', lineHeight: '1.6' }}>
                                                        {authWarning === 'unauthenticated'
                                                                ? "You need to be signed in to submit a support ticket. If you're unable to create an account, reach out to us on Discord."
                                                                : "Your email address needs to be verified before you can submit a support ticket. If you're having trouble, reach out to us on Discord."}
                                                </p>
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                                        {authWarning === 'unauthenticated' ? (
                                                                <>
                                                                        <button onClick={() => navigate('/login')} style={{ background: '#5B92E5', color: 'white', border: 'none', borderRadius: '8px', padding: '10px 20px', cursor: 'pointer', fontWeight: '600', fontSize: '1rem' }}>
                                                                                Log In
                                                                        </button>
                                                                        <button onClick={() => navigate('/register')} style={{ background: 'transparent', color: '#5B92E5', border: '1px solid #5B92E5', borderRadius: '8px', padding: '10px 20px', cursor: 'pointer', fontWeight: '600', fontSize: '1rem' }}>
                                                                                Create Account
                                                                        </button>
                                                                </>
                                                        ) : (
                                                                <button onClick={() => navigate('/verify-email')} style={{ background: '#5B92E5', color: 'white', border: 'none', borderRadius: '8px', padding: '10px 20px', cursor: 'pointer', fontWeight: '600', fontSize: '1rem' }}>
                                                                        Verify Email
                                                                </button>
                                                        )}
                                                        <button onClick={() => window.open('https://discord.gg/DYkESHwgVA', '_blank', 'noopener,noreferrer')} style={{ background: '#5865F2', color: 'white', border: 'none', borderRadius: '8px', padding: '10px 20px', cursor: 'pointer', fontWeight: '600', fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                                                                <i className="fab fa-discord"></i> Join Discord
                                                        </button>
                                                        <button onClick={() => setAuthWarning(null)} style={{ background: 'transparent', color: '#64748b', border: 'none', padding: '8px', cursor: 'pointer', fontSize: '0.875rem' }}>
                                                                Dismiss
                                                        </button>
                                                </div>
                                        </div>
                                </div>
                        )}

                </div>
        )

}

export default SupportTicket